import React, { useCallback, useEffect, useRef, useState } from 'react';
import { memberIdentificationClient } from 'tradera/utils/http';
import {
    Trans,
    useTranslator,
    useCurrentLanguage
} from 'tradera/lang/translate';
import { LocalLoader } from 'tradera/components/loader/loader';
import { GtmService } from 'tradera/services/google-tagmanager-service';
import InfoIcon from '@tradera/blueprint/theme/icons2/info.svg?jsx';

const STATE = {
    VERIFYING: 'VERIFYING',
    ERROR_UNAVAILABLE: 'ERROR_UNAVAILABLE',
    CONTACT_CUSTOMER_SUPPORT: 'CONTACT_CUSTOMER_SUPPORT'
};

const addScriptToDom = (webSdkUrl) => {
    return new Promise((resolve, reject) => {
        const dataAttributeName = 'data-signicat-web-sdk-url-script';
        if (document.querySelector(`[${dataAttributeName}]`)) {
            return resolve();
        }
        const js = document.createElement('script');
        js.setAttribute(dataAttributeName, '1');
        js.setAttribute('type', 'text/javascript');
        js.setAttribute('src', webSdkUrl);
        js.setAttribute('async', true);
        const onLoad = () => {
            js.removeEventListener('load', onLoad);
            return resolve();
        };
        js.addEventListener('load', onLoad);
        js.addEventListener('error', reject);
        document.body.appendChild(js);
    });
};

const useCallbackWrapper = (callback) => {
    const callbackRef = useRef(callback);
    useEffect(() => {
        callbackRef.current = callback;
    }, [callback]);
    return useCallback((...args) => callbackRef.current(...args), []);
};

export const SignicatScanning = ({
    onVerifyComplete: onVerifyCompleteIncoming,
    purpose
}) => {
    const { t } = useTranslator();
    const [isLoading, setIsLoading] = useState(true);

    const onVerifyComplete = useCallbackWrapper(onVerifyCompleteIncoming);
    const [verifyingState, setVerifyingState] = useState(STATE.VERIFYING);
    const { language } = useCurrentLanguage();

    useEffect(() => {
        if (verifyingState !== STATE.VERIFYING) {
            return;
        }

        GtmService.trackAction('DocumentScanning', 'Start', null);
        let currentTimeoutId = null;
        const poll = (sessionId, dossierId) =>
            new Promise((resolve) => {
                const call = () => {
                    // eslint-disable-next-line better-mutation/no-mutation
                    currentTimeoutId = setTimeout(async () => {
                        try {
                            // Result will appear when verification is completed.
                            const result =
                                await memberIdentificationClient.command(
                                    'SignicatSessionStatus',
                                    { purpose, sessionId, dossierId }
                                );

                            if (result.isComplete) {
                                return resolve(result);
                            }
                        } catch (error) {
                            console.error(error);
                        } finally {
                            if (currentTimeoutId) {
                                call();
                            }
                        }
                    }, 2000);
                };
                call();
            });

        setIsLoading(true);
        memberIdentificationClient
            .command('SignicatCreateSession', {})
            .then(async ({ token, onfidoWebSdkUrl, sessionId, dossierId }) => {
                await addScriptToDom(onfidoWebSdkUrl);
                setIsLoading(false);

                return new Promise((resolve) =>
                    setTimeout(
                        () => resolve({ token, sessionId, dossierId }),
                        0
                    )
                );
            })
            .then(async ({ token, sessionId, dossierId }) => {
                return new Promise((resolve, reject) => {
                    window.Onfido.init({
                        token,
                        containerId: 'onfido-mount',
                        steps: [
                            {
                                type: 'document',
                                options: {
                                    hideCountrySelection: true,
                                    documentTypes: {
                                        passport: true,
                                        national_identity_card: true,
                                        driving_licence: true
                                    }
                                }
                            },
                            'face'
                        ],
                        onError: reject,
                        onComplete: () => {
                            resolve({ sessionId, dossierId });
                        },
                        language
                    });
                });
            })
            .then(async ({ sessionId, dossierId }) => {
                setIsLoading(true);
                const { isSuccessful, validationErrors } = await poll(
                    sessionId,
                    dossierId
                );
                setIsLoading(false);

                if (isSuccessful) {
                    GtmService.trackAction('DocumentScanning', 'Success', null);
                    onVerifyComplete();
                } else {
                    if (validationErrors.length > 0) {
                        validationErrors.forEach((error) =>
                            GtmService.trackAction(
                                'DocumentScanning',
                                'ValidationError',
                                error
                            )
                        );
                    }
                    setVerifyingState(STATE.CONTACT_CUSTOMER_SUPPORT);
                }
                return;
            })
            .catch((ex) => {
                console.log(ex);
                GtmService.trackAction(
                    'DocumentScanning',
                    'Error',
                    'ErrorUnavailable'
                );
                setIsLoading(false);
                setVerifyingState(STATE.ERROR_UNAVAILABLE);
            });
        return () => {
            currentTimeoutId && clearTimeout(currentTimeoutId);
            // eslint-disable-next-line better-mutation/no-mutation
            currentTimeoutId = null;
        };
    }, [
        verifyingState,
        setVerifyingState,
        setIsLoading,
        onVerifyComplete,
        purpose,
        language
    ]);

    return (
        <div className="document-scanning w-100 h-100 position-relative d-flex flex-column justify-content-center">
            {isLoading && <LocalLoader />}
            {verifyingState === STATE.VERIFYING && !isLoading && (
                <>
                    <div className="alert alert-info m-2" role="alert">
                        <div className="size-london">
                            <InfoIcon className="icon icon-md" />
                            <strong className="ml-1">
                                {t('international_identification_info_title')}
                            </strong>
                        </div>
                        {t('international_identification_info_text')}
                    </div>
                    <div id="onfido-mount" className="w-100">
                        <link
                            rel="stylesheet"
                            href="https://assets.onfido.com/web-sdk-releases/12.3.3/style.css"
                        />
                    </div>
                </>
            )}
            {verifyingState === STATE.ERROR_UNAVAILABLE && (
                <span className="text-danger p-5">
                    {t('international_identification_errorUnavailable')}
                </span>
            )}
            {verifyingState === STATE.CONTACT_CUSTOMER_SUPPORT && (
                <div className="text-center p-5">
                    <h4 className="my-4">
                        {t(
                            'international_identification_contactCustomerSupportHeading'
                        )}
                    </h4>
                    <p className="mb-4 text-left px-4">
                        <Trans
                            i18nKey="international_identification_contactCustomerSupportText_Trans"
                            // eslint-disable-next-line jsx-a11y/anchor-is-valid
                            components={{ a: <a /> }}
                        />
                    </p>
                    <button
                        className="btn btn-primary"
                        onClick={() => setVerifyingState(STATE.VERIFYING)}>
                        {t('international_identification_buttonTryAgain')}
                    </button>
                </div>
            )}
        </div>
    );
};
